import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
    Mic, 
    MicOff, 
    Play, 
    Square, 
    ChevronRight, 
    CheckCircle2, 
    ArrowRight,
    Loader2,
    X,
    TrendingUp,
    Clock,
    Volume2,
    MessageSquare
} from 'lucide-react';

const VoiceProfileAssessmentPopup = ({ onComplete, onDismiss, forceShow = false }) => {
    const { api } = useAuth();
    
    // State
    const [open, setOpen] = useState(forceShow);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(!forceShow);
    const [stage, setStage] = useState('intro'); // intro, assessment, recording, processing, complete
    
    // Assessment state
    const [assessmentId, setAssessmentId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [responses, setResponses] = useState([]);
    const [result, setResult] = useState(null);
    
    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    
    // Refs
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const streamRef = useRef(null);

    // Check assessment status on mount (skip if forceShow)
    useEffect(() => {
        if (!forceShow) {
            checkAssessmentStatus();
        } else {
            // When forceShow, set status to needs_assessment
            setStatus({ needs_assessment: true, reason: 'first_time' });
        }
    }, [forceShow]);

    const checkAssessmentStatus = async () => {
        try {
            setLoading(true);
            const response = await api.get('/voice-profile-assessment/status');
            setStatus(response.data);
            
            if (response.data.needs_assessment) {
                setOpen(true);
            }
        } catch (error) {
            console.error('Failed to check assessment status:', error);
        } finally {
            setLoading(false);
        }
    };

    const startAssessment = async () => {
        try {
            const [startRes, questionsRes] = await Promise.all([
                api.post('/voice-profile-assessment/start'),
                api.get('/voice-profile-assessment/questions')
            ]);
            
            setAssessmentId(startRes.data.assessment_id);
            setQuestions(questionsRes.data.questions);
            setCurrentQuestionIndex(0);
            setResponses([]);
            setStage('assessment');
            
        } catch (error) {
            toast.error('Failed to start assessment');
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            
            const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];
            
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            
            mediaRecorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                setAudioBlob(blob);
                setStage('processing');
                submitResponse(blob);
            };
            
            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            setStage('recording');
            
            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
            
        } catch (error) {
            toast.error('Please allow microphone access to continue');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        }
    };

    const submitResponse = async (blob) => {
        try {
            const question = questions[currentQuestionIndex];
            const formData = new FormData();
            formData.append('audio', blob, 'response.webm');
            
            const response = await api.post(
                `/voice-profile-assessment/${assessmentId}/submit-response?question_id=${question.id}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            
            setResponses(prev => [...prev, response.data]);
            
            // Move to next question or complete
            if (currentQuestionIndex < questions.length - 1) {
                setCurrentQuestionIndex(prev => prev + 1);
                setAudioBlob(null);
                setStage('assessment');
            } else {
                // Complete assessment
                completeAssessment();
            }
            
        } catch (error) {
            toast.error('Failed to submit response. Please try again.');
            setStage('assessment');
        }
    };

    const completeAssessment = async () => {
        try {
            setStage('processing');
            const response = await api.post(`/voice-profile-assessment/${assessmentId}/complete`);
            setResult(response.data);
            setStage('complete');
            
            if (onComplete) {
                onComplete(response.data);
            }
        } catch (error) {
            toast.error('Failed to complete assessment');
            setStage('assessment');
        }
    };

    const handleDismiss = async () => {
        try {
            await api.post('/voice-profile-assessment/dismiss');
            setOpen(false);
            if (onDismiss) {
                onDismiss();
            }
        } catch (error) {
            setOpen(false);
        }
    };

    const handleClose = () => {
        if (stage === 'complete') {
            setOpen(false);
        } else {
            handleDismiss();
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const currentQuestion = questions[currentQuestionIndex];
    const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

    if (loading || !status?.needs_assessment) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={(newOpen) => !newOpen && handleClose()}>
            <DialogContent className="sm:max-w-lg" data-testid="voice-assessment-popup">
                {/* Close button */}
                <button
                    onClick={handleDismiss}
                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
                >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                </button>

                {/* Intro Stage */}
                {stage === 'intro' && (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-xl">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Mic className="h-5 w-5 text-primary" />
                                </div>
                                Voice Profile Assessment
                            </DialogTitle>
                            <DialogDescription className="text-base">
                                {status?.reason === 'first_time' 
                                    ? "Complete a quick voice assessment to help us understand your communication style and create a personalized learning path."
                                    : status?.message
                                }
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            <Card className="border-primary/20 bg-primary/5">
                                <CardContent className="pt-4 space-y-3">
                                    <div className="flex items-center gap-3">
                                        <Clock className="h-5 w-5 text-primary" />
                                        <span className="text-sm">Takes only ~2 minutes</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <MessageSquare className="h-5 w-5 text-primary" />
                                        <span className="text-sm">5-7 voice questions</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Volume2 className="h-5 w-5 text-primary" />
                                        <span className="text-sm">Verbal responses only - no typing</span>
                                    </div>
                                    {status?.reason === 'periodic_reassessment' && (
                                        <div className="flex items-center gap-3">
                                            <TrendingUp className="h-5 w-5 text-emerald-500" />
                                            <span className="text-sm">Track your improvement over time</span>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <div className="flex gap-3">
                                <Button 
                                    variant="outline" 
                                    onClick={handleDismiss}
                                    className="flex-1"
                                >
                                    Remind Me Later
                                </Button>
                                <Button 
                                    onClick={startAssessment}
                                    className="flex-1 btn-primary"
                                    data-testid="start-voice-assessment"
                                >
                                    Start Assessment
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </div>
                        </div>
                    </>
                )}

                {/* Assessment Stage */}
                {(stage === 'assessment' || stage === 'recording') && currentQuestion && (
                    <>
                        <DialogHeader>
                            <div className="flex items-center justify-between">
                                <DialogTitle className="text-lg">
                                    Question {currentQuestionIndex + 1} of {questions.length}
                                </DialogTitle>
                                <Badge variant="outline">{currentQuestion.prompt_type}</Badge>
                            </div>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            <Progress value={progress} className="h-2" />
                            
                            <Card>
                                <CardContent className="pt-4">
                                    <p className="text-base font-medium mb-4">
                                        {currentQuestion.question}
                                    </p>
                                    
                                    {currentQuestion.hints && (
                                        <div className="text-xs text-muted-foreground space-y-1">
                                            {currentQuestion.hints.map((hint, i) => (
                                                <p key={i}>💡 {hint}</p>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Recording Controls */}
                            <div className="text-center space-y-4">
                                {stage === 'recording' && (
                                    <div className="flex items-center justify-center gap-2 text-red-500">
                                        <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                                        <span className="font-medium">{formatTime(recordingTime)}</span>
                                    </div>
                                )}

                                <div className="flex justify-center">
                                    {!isRecording ? (
                                        <Button
                                            size="lg"
                                            onClick={startRecording}
                                            className="rounded-full h-16 w-16 bg-primary hover:bg-primary/90"
                                            data-testid="start-recording"
                                        >
                                            <Mic className="h-6 w-6" />
                                        </Button>
                                    ) : (
                                        <Button
                                            size="lg"
                                            onClick={stopRecording}
                                            variant="destructive"
                                            className="rounded-full h-16 w-16"
                                            data-testid="stop-recording"
                                        >
                                            <Square className="h-6 w-6" />
                                        </Button>
                                    )}
                                </div>

                                <p className="text-sm text-muted-foreground">
                                    {isRecording 
                                        ? "Click to stop when you're done" 
                                        : "Click the microphone to start recording"
                                    }
                                </p>
                            </div>
                        </div>
                    </>
                )}

                {/* Processing Stage */}
                {stage === 'processing' && (
                    <div className="py-8 text-center space-y-4">
                        <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
                        <p className="text-lg font-medium">Analyzing your response...</p>
                        <p className="text-sm text-muted-foreground">
                            Our AI is evaluating your voice and communication skills
                        </p>
                    </div>
                )}

                {/* Complete Stage */}
                {stage === 'complete' && result && (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-xl">
                                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                                Assessment Complete!
                            </DialogTitle>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            <div className="grid grid-cols-2 gap-4">
                                <Card>
                                    <CardContent className="pt-4 text-center">
                                        <p className="text-3xl font-bold text-primary">
                                            {result.overall_scores?.overall_voice || 0}
                                        </p>
                                        <p className="text-sm text-muted-foreground">Voice Score</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="pt-4 text-center">
                                        <p className="text-3xl font-bold text-primary">
                                            {result.overall_scores?.overall_communication || 0}
                                        </p>
                                        <p className="text-sm text-muted-foreground">Communication</p>
                                    </CardContent>
                                </Card>
                            </div>

                            {result.improvement && (
                                <Card className={result.improvement.improved ? 'border-emerald-500/50 bg-emerald-50/50' : 'border-yellow-500/50 bg-yellow-50/50'}>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center gap-2">
                                            <TrendingUp className={`h-5 w-5 ${result.improvement.improved ? 'text-emerald-500' : 'text-yellow-500'}`} />
                                            <span className="font-medium">
                                                {result.improvement.improved 
                                                    ? `Improved by ${result.improvement.change} points!`
                                                    : `${Math.abs(result.improvement.change)} points from last time`
                                                }
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            <Button onClick={() => setOpen(false)} className="w-full">
                                Continue to Dashboard
                                <ChevronRight className="h-4 w-4 ml-2" />
                            </Button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default VoiceProfileAssessmentPopup;
